"use client";

import React, { useEffect, useRef, useState } from "react";
interface CardGridProps {
  children: React.ReactNode;
  className?: string;
  minCardWidth?: number; // Minimum width of each card
  gap?: number; // Gap between cards
  padding?: number; // Padding for the grid container
  maxColumns?: number; // Maximum columns allowed
  gapY?: number; // Vertical gap between rows
}

// Your original JS-based CardGrid with ResizeObserver
const CardGridJS: React.FC<CardGridProps> = ({
  children,
  minCardWidth = 250,
  className,
  gap = 15,
  padding = 0,
  maxColumns = 4,
  gapY,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);

  const calculateColumns = () => {
    if (!gridRef.current) return;

    const containerWidth = gridRef.current.offsetWidth - padding * 2;
    const potentialColumns = Math.floor(containerWidth / (minCardWidth + gap));
    setColumns(Math.max(1, Math.min(potentialColumns, maxColumns)));
  };

  useEffect(() => {
    calculateColumns();

    const resizeObserver = new ResizeObserver(() => calculateColumns());
    if (gridRef.current) resizeObserver.observe(gridRef.current);

    return () => resizeObserver.disconnect();
  }, [minCardWidth, gap, padding, maxColumns]);

  return (
    <div
      ref={gridRef}
      className={`grid w-full ${className}`}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: `${gap}px`,
        rowGap: `${gapY}px`,
        padding: `${padding}px`,
      }}
    >
      {React.Children.map(children, (child) => {
        if (!child) return null; // Skip null, undefined, false, etc.

        return (
          <div
            style={{
              minWidth: minCardWidth,
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

const CardGrid: React.FC<CardGridProps> = (props) => {
  return <CardGridJS {...props} />;
};

export default CardGrid;
